import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Mapa de categorias de comissão derivado dinamicamente da Matriz Produto x KPIs
 * (tabela `produtos`). Cada chave (PN/HA/FIBRA/TT/MP) lista os NOMES de produto
 * cujo faturamento entra no respectivo balde de comissão.
 *
 * Regras (alinhadas à Matriz Produto x KPIs):
 *   PN    → fat_pn = true                                          (PN NOVO, PN INCREMENTO)
 *   HA    → fat_movel = true AND fat_pn = false                    (HA NOVO, HA INCREMENTO)
 *   FIBRA → fat_fibra = true                                       (FIBRA, TV)
 *   TT    → fat_migracao = true AND nome ILIKE 'TT%'               (TT POS-PJ, TT PRE-PJ)
 *   MP    → fat_mp_movel = true                                    (RENOVAÇAO)
 *
 * Listas de quantidade (para metas):
 *   altas    → qtd_altas = true
 *   fibras   → qtd_fibra = true (TV NÃO entra)
 *   tt       → mesmos da categoria TT
 *   mp       → qtd_mp_movel = true
 *   migracao → qtd_mp_movel_fixa_up = true (RENOVAÇAO + TT)
 *
 * Produtos com `comissionado=false` (TT FIXA, TT PJ - PJ) NÃO aparecem em
 * nenhuma categoria de comissão e NÃO somam em faturamento — exatamente como
 * pedido pelo gestor.
 */
export type CategoriaComissao = 'PN' | 'HA' | 'FIBRA' | 'TT' | 'TTPRE' | 'MP';
export type CategoriasMap = Record<CategoriaComissao, string[]>;

export interface ProdutosCategorias {
  categoriasMap: CategoriasMap;
  produtosAlta: string[];
  produtosFibra: string[];
  produtosTt: string[];
  produtosMp: string[];
  produtosMigracao: string[];
  /**
   * Produtos que somam FATURAMENTO — o KPI `FAT. TOTAL` do cadastro, e só ele.
   * É a régua única de R$: fora dela ficam os NÃO PAGO (AGENDA AV, TT FIXA,
   * TOTALIZAÇÃO…) e o APARELHO, que tem KPI próprio (`FAT. APARELHOS`) e não é
   * faturamento de VENDA NOVA nem de MIGRAÇÃO.
   */
  produtosFatTotal: string[];
  /** (Douglas 28/07) Aglomerado de T.I. — produtos com `fat_ti`. */
  produtosTi: string[];
  /** (Douglas 28/07) Aglomerado de Fixa Avançada — produtos com `fat_avancada`. */
  produtosAvancada: string[];
  /** (Douglas 29/08) Serviços Digitais — produtos com `qtd_sd` (1 licença = 1 SD). */
  produtosSd: string[];
  /** (Douglas 21/10/2026) Produtos com qtd_tv (TV CPF) — meta de TV do CPF. */
  produtosTv: string[];
  /** Lookup rápido nome → categoria (ou null se não comissionado) */
  getCategoria: (produtoNome: string) => CategoriaComissao | null;
}

const EMPTY: ProdutosCategorias = {
  categoriasMap: { PN: [], HA: [], FIBRA: [], TT: [], TTPRE: [], MP: [] },
  produtosAlta: [],
  produtosFibra: [],
  produtosTt: [],
  produtosMp: [],
  produtosMigracao: [],
  produtosFatTotal: [],
  produtosTi: [],
  produtosAvancada: [],
  produtosSd: [],
  produtosTv: [],
  getCategoria: () => null,
};

export function useProdutosCategorias() {
  return useQuery<ProdutosCategorias>({
    queryKey: ['produtos-categorias-comissao'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('produtos')
        .select('nome, comissionado, fat_total, fat_pn, fat_movel, fat_fibra, fat_migracao, fat_mp_movel, fat_ti, fat_avancada, qtd_altas, qtd_fibra, qtd_mp_movel, qtd_mp_movel_fixa_up, qtd_sd, qtd_tv')
        .eq('ativo', true);
      if (error) throw error;

      const categoriasMap: CategoriasMap = { PN: [], HA: [], FIBRA: [], TT: [], TTPRE: [], MP: [] };
      const produtosAlta: string[] = [];
      const produtosFibra: string[] = [];
      const produtosTt: string[] = [];
      const produtosMp: string[] = [];
      const produtosMigracao: string[] = [];
      const produtosFatTotal: string[] = [];
      const produtosTi: string[] = [];
      const produtosAvancada: string[] = [];
      const produtosSd: string[] = [];
      const produtosTv: string[] = [];

      for (const p of data || []) {
        const nome = p.nome as string;

        // Categorias de comissão — só produtos comissionados entram
        if (p.comissionado) {
          if (p.fat_pn) {
            categoriasMap.PN.push(nome);
          } else if (p.fat_movel) {
            categoriasMap.HA.push(nome);
          }
          if (p.fat_fibra) {
            categoriasMap.FIBRA.push(nome);
          }
          // TT (categoria comissão): produtos de migração cujo nome começa com TT.
          // PRE → balde TT PRE (TT PRE-PJ); demais → TT POS (TT POS-PJ, TT MÓVEL).
          if (p.fat_migracao && /^TT/i.test(nome)) {
            if (/PRE/i.test(nome)) categoriasMap.TTPRE.push(nome);
            else categoriasMap.TT.push(nome);
            produtosTt.push(nome);
          }
          if (p.fat_mp_movel) {
            categoriasMap.MP.push(nome);
          }
        }

        // Quantidades para metas (independem de comissionado, mas na prática
        // os flags qtd_* só estão true em produtos comissionados)
        if (p.qtd_altas) produtosAlta.push(nome);
        if (p.qtd_fibra) produtosFibra.push(nome);
        if (p.qtd_mp_movel) produtosMp.push(nome);
        if (p.qtd_mp_movel_fixa_up) produtosMigracao.push(nome);
        if (p.fat_total) produtosFatTotal.push(nome);
        // (Douglas 28/07) T.I. e Fixa Avançada vêm do KPI da Matriz Produto × KPIs,
        // FORA do `if (comissionado)` — igual ao que o Excel já faz. Marcar/desmarcar
        // na Matriz muda meta e pagamento sozinho, sem deploy.
        if (p.fat_ti) produtosTi.push(nome);
        if (p.fat_avancada) produtosAvancada.push(nome);
        // (Douglas 29/08) SD sai da Matriz (qtd_sd) — mesma régua da Venda
        // Diária: cada linha de licença soma `quantidade`.
        if ((p as { qtd_sd?: boolean }).qtd_sd) produtosSd.push(nome);
        if ((p as { qtd_tv?: boolean }).qtd_tv) produtosTv.push(nome);
      }

      // Lookup invertido nome → categoria
      const reverse = new Map<string, CategoriaComissao>();
      (Object.keys(categoriasMap) as CategoriaComissao[]).forEach(cat => {
        for (const nome of categoriasMap[cat]) {
          // PN tem prioridade sobre HA (já tratado no if/else acima)
          if (!reverse.has(nome)) reverse.set(nome, cat);
        }
      });

      return {
        categoriasMap,
        produtosAlta,
        produtosFibra,
        produtosTt,
        produtosMp,
        produtosMigracao,
        produtosFatTotal,
        produtosTi,
        produtosAvancada,
        produtosSd,
        produtosTv,
        getCategoria: (nome: string) => reverse.get(nome) ?? null,
      };
    },
    staleTime: 10 * 60 * 1000,
    placeholderData: EMPTY,
  });
}
